export type DemandMetric = {
  label: string
  value: string
  detail: string
}

export const demandMetrics: DemandMetric[] = [
  { label: 'Attributed revenue', value: '$184,920', detail: 'Last 30 days across paid and organic' },
  { label: 'Units moved', value: '1,842', detail: 'Driven by demand activity' },
  { label: 'Blended ROAS', value: '4.2x', detail: '$44,100 spend in the same window' },
  { label: 'Audience reached', value: '612K', detail: 'Unique reach across all channels' },
]

export type Opportunity = {
  id: string
  title: string
  brand: string
  units: number
  daysLive: number
  sellThrough: number
  forecast: number
  recovery: string
  action: string
  reason: string
  priority: 'High' | 'Medium' | 'Watch'
}

export const opportunities: Opportunity[] = [
  {
    id: 'op-1',
    title: 'Chelsea Boot — Imperfects',
    brand: 'Nisolo',
    units: 412,
    daysLive: 34,
    sellThrough: 18,
    forecast: 41,
    recovery: '$21,400',
    action: 'Run a paid push on Meta and Vibe',
    reason: 'Full marketplace exposure without conversion. Reach is the constraint, not price.',
    priority: 'High',
  },
  {
    id: 'op-2',
    title: 'Summer capsule dresses',
    brand: 'Eddy',
    units: 187,
    daysLive: 21,
    sellThrough: 26,
    forecast: 38,
    recovery: '$8,400',
    action: 'Expand to eBay and Whatnot',
    reason: 'Converting well where listed, but only live on two of five approved channels.',
    priority: 'High',
  },
  {
    id: 'op-3',
    title: 'Oxford — Damages',
    brand: 'Nisolo',
    units: 268,
    daysLive: 12,
    sellThrough: 9,
    forecast: 22,
    recovery: '$6,900',
    action: 'Bundle with belts in a paired listing',
    reason: 'C-grade units move slowly alone. Pairing lifts perceived value at the same price.',
    priority: 'Medium',
  },
  {
    id: 'op-4',
    title: 'Outerwear — August returns',
    brand: '1 People',
    units: 143,
    daysLive: 8,
    sellThrough: 31,
    forecast: 29,
    recovery: '$4,200',
    action: 'Hold current plan',
    reason: 'Tracking ahead of forecast. Carli is monitoring before recommending spend.',
    priority: 'Watch',
  },
]

export type AdAngle = {
  id: string
  name: string
  headline: string
  body: string
  cta: string
  visual: string
}

export const adAngles: AdAngle[] = [
  {
    id: 'value',
    name: 'Value-led',
    headline: 'The boot you know, 45% off',
    body: 'Small imperfections. Same leather, same last, same resole-ready construction. Limited pairs at this price.',
    cta: 'Shop imperfects',
    visual: 'Clean product-on-neutral shot with a price flag in the corner.',
  },
  {
    id: 'story',
    name: 'Sustainability story',
    headline: 'Too good to write off',
    body: 'These pairs missed a quality check by millimeters. Rather than discard them, we priced them to be worn.',
    cta: 'Give them a home',
    visual: 'Warm lifestyle photo, boots worn outdoors, natural light.',
  },
  {
    id: 'scarcity',
    name: 'Scarcity',
    headline: '412 pairs. No restock.',
    body: 'Imperfect stock is a one-time run. When a size goes, it does not come back.',
    cta: 'Check your size',
    visual: 'Grid of size chips with several visibly sold out.',
  },
]

export type Campaign = {
  id: string
  name: string
  brand: string
  channel: string
  status: 'Live' | 'Scheduled' | 'Completed' | 'Paused'
  window: string
  spend: number
  revenue: number
  units: number
}

export const campaigns: Campaign[] = [
  { id: 'c1', name: 'Imperfects launch — paid social', brand: 'Nisolo', channel: 'Meta', status: 'Live', window: 'Aug 4 – Sep 1', spend: 12400, revenue: 58200, units: 486 },
  { id: 'c2', name: 'Vibe ad placement — footwear', brand: 'Nisolo', channel: 'Vibe', status: 'Live', window: 'Aug 11 – Sep 8', spend: 8600, revenue: 31400, units: 268 },
  { id: 'c3', name: 'Summer capsule flow', brand: 'Eddy', channel: 'Klaviyo', status: 'Live', window: 'Jul 28 – ongoing', spend: 2100, revenue: 24800, units: 312 },
  { id: 'c4', name: 'Fall partnership push', brand: 'Eddy', channel: 'Manymoons', status: 'Scheduled', window: 'Sep 2 – Sep 30', spend: 0, revenue: 0, units: 0 },
  { id: 'c5', name: 'Outerwear retargeting', brand: '1 People', channel: 'Meta', status: 'Paused', window: 'Jul 1 – Jul 22', spend: 6300, revenue: 18900, units: 164 },
  { id: 'c6', name: 'Spring clearance — live selling', brand: 'Eddy', channel: 'Whatnot', status: 'Completed', window: 'Jun 3 – Jun 30', spend: 4700, revenue: 27600, units: 402 },
]

export type SocialPost = {
  id: string
  channel: string
  caption: string
  posted: string
  reach: number
  engagement: number
  clicks: number
  status: 'Published' | 'Scheduled' | 'Draft'
}

export const socialPosts: SocialPost[] = [
  { id: 's1', channel: 'Instagram', caption: 'Imperfect by a millimeter. Priced like it matters.', posted: 'Aug 19', reach: 84200, engagement: 6.4, clicks: 2180, status: 'Published' },
  { id: 's2', channel: 'TikTok', caption: 'What actually makes a pair a "second"? A walkthrough.', posted: 'Aug 16', reach: 142000, engagement: 9.1, clicks: 4310, status: 'Published' },
  { id: 's3', channel: 'Instagram', caption: 'Partnership spotlight: how Manymoons finds these homes.', posted: 'Aug 12', reach: 51800, engagement: 4.8, clicks: 1120, status: 'Published' },
  { id: 's4', channel: 'TikTok', caption: 'Size run check — what is left in the imperfects drop.', posted: 'Aug 24', reach: 0, engagement: 0, clicks: 0, status: 'Scheduled' },
  { id: 's5', channel: 'Instagram', caption: 'Behind the resole: why these are worth keeping.', posted: '—', reach: 0, engagement: 0, clicks: 0, status: 'Draft' },
]
